import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import {
    IconCheck, IconPlus, IconDeviceFloppy, IconX, IconTrash,
    IconGripVertical, IconSeparator, IconChevronDown, IconCircleX, IconCopy,
} from '@tabler/icons-react';
import {
    DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
    SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { notifications } from '../../lib/notifications';
import templateSetService from '../../services/templateSetService';

const PLACEHOLDER_TYPES = [
    { value: 'Text',         label: 'Text' },
    { value: 'Textarea',     label: 'Textarea (Multiline)' },
    { value: 'Number',       label: 'Number' },
    { value: 'Email',        label: 'Email' },
    { value: 'Phone',        label: 'Phone' },
    { value: 'Date',         label: 'Date' },
    { value: 'DateBar',      label: 'Date + Day Name (তারিখ ও বার)' },
    { value: 'Currency',     label: 'Currency (অঙ্ক + কথায়)' },
    { value: 'Select',       label: 'Dropdown (Select)' },
    { value: 'Options',      label: 'Options (Badge Picker)' },
    { value: 'LandUnit',     label: 'Land Unit (জমির পরিমাণ + ইউনিট)' },
    { value: 'YesNo',        label: 'Yes / No (হ্যাঁ / না)' },
    { value: 'CustomToggle', label: 'Custom Toggle (রেডিও বাটন)' },
    { value: 'Occupation',   label: 'Occupation (পেশা)' },
    { value: 'Nationality',  label: 'Nationality (জাতীয়তা)' },
    { value: 'Religion',     label: 'Religion (ধর্ম)' },
    { value: 'TableBuilder', label: 'Table Builder' },
    { value: 'BulletList',   label: 'Bullet List' },
];

// Types whose rendering depends on a comma-separated `options` string.
const TYPES_NEEDING_OPTIONS = new Set(['Select', 'CustomToggle', 'Options']);

// ── Sortable placeholder row ──────────────────────────────────────────────────
function SortablePlaceholderItem({ item, saving, onUpdate, onSave, onDelete }) {
    const [expanded, setExpanded] = useState(false);

    const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
        useSortable({ id: item.slug });

    const wrapStyle = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
        position: 'relative',
        zIndex: isDragging ? 999 : 'auto',
    };

    const gripProps = {
        ...attributes,
        ...listeners,
        onPointerDown: (e) => { e.stopPropagation(); listeners?.onPointerDown?.(e); },
        onClick: (e) => e.stopPropagation(),
        style: { cursor: isDragging ? 'grabbing' : 'grab', touchAction: 'none', flexShrink: 0, display: 'flex', alignItems: 'center', padding: '2px 4px', borderRadius: 4 },
    };

    // Step break row
    if (item.type === 'Steps') {
        return (
            <div ref={setNodeRef} style={wrapStyle}>
                <div className="rounded-lg border border-dashed border-purple-400 bg-purple-50 dark:bg-purple-900/10 px-2 py-1.5">
                    <div className="flex items-center gap-2 flex-nowrap">
                        <div {...gripProps}><IconGripVertical size={14} className="text-purple-500" /></div>
                        <IconSeparator size={13} className="text-purple-500 flex-shrink-0" />
                        <input
                            type="text"
                            placeholder="Step title shown to user"
                            className="flex-1 min-w-0 px-2 py-1 text-xs rounded-md border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 focus:outline-none focus:ring-2 focus:ring-orange-500"
                            value={item.title}
                            onChange={e => onUpdate('title', e.target.value)}
                        />
                        <button
                            title="Save step title"
                            className="p-1 rounded hover:bg-violet-100 dark:hover:bg-violet-900/20 text-violet-600 transition-colors disabled:opacity-50"
                            disabled={saving}
                            onClick={onSave}
                        >
                            {saving ? (
                                <span className="w-3 h-3 border-2 border-violet-500 border-t-transparent rounded-full animate-spin block" />
                            ) : (
                                <IconDeviceFloppy size={13} />
                            )}
                        </button>
                        <button
                            title="Delete step break"
                            className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500 transition-colors"
                            onClick={onDelete}
                        >
                            <IconCircleX size={14} />
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Regular placeholder row
    return (
        <div ref={setNodeRef} style={wrapStyle}>
            <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 overflow-hidden">
                <div
                    className="flex items-center gap-2 px-2 py-1.5 flex-nowrap cursor-pointer select-none hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors"
                    onClick={() => setExpanded(v => !v)}
                >
                    <div {...gripProps}><IconGripVertical size={14} className="text-zinc-400" /></div>
                    <button
                        title={`Copy {{${item.slug}}}`}
                        className="p-0.5 rounded hover:bg-zinc-100 dark:hover:bg-zinc-700 text-zinc-400 flex-shrink-0"
                        onClick={e => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(`{{${item.slug}}}`);
                            notifications.show({ message: `Copied {{${item.slug}}}`, color: 'green', autoClose: 1500 });
                        }}
                    >
                        <IconCopy size={12} />
                    </button>
                    <code className="font-mono text-xs bg-zinc-100 dark:bg-zinc-800 px-1.5 py-0.5 rounded flex-shrink-0">{`{{${item.slug}}}`}</code>
                    {item.title && (
                        <span className="text-xs text-zinc-500 dark:text-zinc-400 flex-1 overflow-hidden text-ellipsis whitespace-nowrap min-w-0">
                            {item.title}
                        </span>
                    )}
                    <IconChevronDown
                        size={14}
                        className="text-zinc-400 flex-shrink-0 ml-auto transition-transform duration-150"
                        style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
                    />
                </div>
                {expanded && (
                    <div className="flex flex-col gap-2 px-3 pb-3 pt-1 border-t border-zinc-200 dark:border-zinc-700">
                        {/* Label */}
                        <div className="flex flex-col gap-1">
                            <label className="text-xs font-medium text-zinc-700 dark:text-zinc-300">Label</label>
                            <input
                                type="text"
                                placeholder={item.slug}
                                className="w-full px-2.5 py-1.5 text-sm rounded-md border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 focus:outline-none focus:ring-2 focus:ring-orange-500"
                                value={item.title}
                                onChange={e => onUpdate('title', e.target.value)}
                            />
                        </div>
                        {/* Type */}
                        <div className="flex flex-col gap-1">
                            <label className="text-xs font-medium text-zinc-700 dark:text-zinc-300">Type</label>
                            <select
                                className="w-full px-2.5 py-1.5 text-sm rounded-md border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 focus:outline-none focus:ring-2 focus:ring-orange-500"
                                value={item.type || 'Text'}
                                onChange={e => onUpdate('type', e.target.value)}
                            >
                                {PLACEHOLDER_TYPES.map(pt => (
                                    <option key={pt.value} value={pt.value}>{pt.label}</option>
                                ))}
                            </select>
                        </div>
                        {/* Options (for relevant types) */}
                        {TYPES_NEEDING_OPTIONS.has(item.type) && (
                            <div className="flex flex-col gap-1">
                                <label className="text-xs font-medium text-zinc-700 dark:text-zinc-300">Options</label>
                                <input
                                    type="text"
                                    placeholder="Option 1, Option 2, Option 3"
                                    className="w-full px-2.5 py-1.5 text-sm rounded-md border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 focus:outline-none focus:ring-2 focus:ring-orange-500"
                                    value={item.options}
                                    onChange={e => onUpdate('options', e.target.value)}
                                />
                                <p className="text-xs text-zinc-500 dark:text-zinc-400">
                                    {item.type === 'CustomToggle'
                                        ? 'Comma-separated toggle values. User picks one; the chosen label is inserted into the document.'
                                        : item.type === 'Options'
                                            ? 'Comma-separated options. First value pre-fills the field; user can type freely or click a badge to pick.'
                                            : 'Comma-separated list of dropdown options'}
                                </p>
                            </div>
                        )}
                        {/* Description */}
                        <div className="flex flex-col gap-1">
                            <label className="text-xs font-medium text-zinc-700 dark:text-zinc-300">Description</label>
                            <textarea
                                placeholder="Hint shown to the user filling this field"
                                rows={2}
                                className="w-full px-2.5 py-1.5 text-sm rounded-md border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
                                value={item.description}
                                onChange={e => onUpdate('description', e.target.value)}
                            />
                        </div>
                        {/* Actions */}
                        <div className="flex items-center justify-between">
                            <button
                                title="Delete placeholder"
                                className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500 transition-colors"
                                onClick={onDelete}
                            >
                                <IconTrash size={14} />
                            </button>
                            <button
                                title="Save"
                                className="p-1.5 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 text-blue-600 transition-colors disabled:opacity-50"
                                disabled={saving}
                                onClick={onSave}
                            >
                                {saving ? (
                                    <span className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin block" />
                                ) : (
                                    <IconDeviceFloppy size={14} />
                                )}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// ── Main PlaceholderManager component ────────────────────────────────────────
/**
 * Props:
 *   templateSetId  — the template set whose placeholders to manage
 */
const PlaceholderManager = forwardRef(function PlaceholderManager({ templateSetId, onDirtyChange }, ref) {
    const [placeholderList, setPlaceholderList] = useState([]);
    const placeholderListRef = useRef([]);
    const [placeholderSaving, setPlaceholderSaving] = useState({});
    const [reordering, setReordering] = useState(false);
    const [loading, setLoading] = useState(false);
    const [dirty, setDirty] = useState(false);

    useEffect(() => { placeholderListRef.current = placeholderList; }, [placeholderList]);

    useEffect(() => {
        onDirtyChange?.(dirty);
    }, [dirty, onDirtyChange]);

    const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

    const loadPlaceholders = async () => {
        if (!templateSetId) return;
        setLoading(true);
        try {
            const res = await templateSetService.getTemplateSetById(templateSetId);
            const list = (res.data?.placeholders || [])
                .sort((a, b) => a.order - b.order)
                .map(p => ({
                    slug: p.slug,
                    title: p.title && p.title !== p.slug ? p.title : '',
                    type: p.type || 'Text',
                    description: p.description || '',
                    options: p.options || '',
                }));
            setPlaceholderList(list);
        } catch (err) {
            notifications.show({ title: 'Failed to load placeholders', message: err.message, color: 'red' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadPlaceholders();
    }, [templateSetId]);

    // Bulk-save every placeholder currently in the list — exposed to parents via ref.
    const saveAll = async () => {
        const list = placeholderListRef.current;
        if (!templateSetId || list.length === 0) {
            setDirty(false);
            return { ok: true, failed: [] };
        }
        const results = await Promise.allSettled(
            list.map((item, i) =>
                templateSetService.updatePlaceholderMetadata(templateSetId, item.slug, {
                    title: item.title || item.slug,
                    type: item.type || 'Text',
                    description: item.description || '',
                    options: item.options || '',
                    order: i + 1,
                })
            )
        );
        const failed = results
            .map((r, i) => ({ r, slug: list[i]?.slug }))
            .filter(x => x.r.status === 'rejected')
            .map(x => ({ slug: x.slug, message: x.r.reason?.message || 'error' }));
        if (failed.length === 0) setDirty(false);
        return { ok: failed.length === 0, failed };
    };

    useImperativeHandle(ref, () => ({
        saveAll,
        isDirty: () => dirty,
        reload: loadPlaceholders,
    }), [templateSetId, dirty]);

    const handlePlaceholderSave = async (slug) => {
        const item = placeholderList.find(p => p.slug === slug);
        if (!item) return;
        setPlaceholderSaving(prev => ({ ...prev, [slug]: true }));
        try {
            await templateSetService.updatePlaceholderMetadata(templateSetId, slug, {
                title: item.title || slug,
                type: item.type || 'Text',
                description: item.description || '',
                options: item.options || '',
            });
            notifications.show({ title: 'Saved', color: 'green', icon: <IconCheck size={14} />, autoClose: 1500 });
        } catch (err) {
            notifications.show({ title: 'Save failed', message: err.message, color: 'red' });
        } finally {
            setPlaceholderSaving(prev => ({ ...prev, [slug]: false }));
        }
    };

    const handleDeletePlaceholder = async (slug) => {
        setPlaceholderSaving(prev => ({ ...prev, [slug]: true }));
        try {
            await templateSetService.deletePlaceholder(templateSetId, slug);
            setPlaceholderList(prev => prev.filter(p => p.slug !== slug));
            notifications.show({ title: 'Placeholder removed', color: 'orange', autoClose: 1500 });
        } catch (err) {
            notifications.show({ title: 'Failed to delete', message: err.message, color: 'red' });
        } finally {
            setPlaceholderSaving(prev => ({ ...prev, [slug]: false }));
        }
    };

    const handleDragEnd = async (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        setPlaceholderList(prev => {
            const oldIndex = prev.findIndex(p => p.slug === active.id);
            const newIndex = prev.findIndex(p => p.slug === over.id);
            const next = arrayMove(prev, oldIndex, newIndex);
            persistOrder(next);
            return next;
        });
    };

    const persistOrder = async (newList) => {
        if (!templateSetId) return;
        setReordering(true);
        try {
            await templateSetService.reorderPlaceholders(
                templateSetId,
                newList.map((p, i) => ({ slug: p.slug, order: i + 1 }))
            );
        } catch (err) {
            notifications.show({ title: 'Reorder failed', message: err.message, color: 'red' });
        } finally {
            setReordering(false);
        }
    };

    const handleAddStepBreak = async () => {
        const stepCount = placeholderList.filter(p => p.type === 'Steps').length + 1;
        const slug = `step-${stepCount}-${Date.now()}`;
        const newItem = { slug, title: `Step ${stepCount}`, type: 'Steps', description: '', options: '' };
        setPlaceholderSaving(prev => ({ ...prev, [slug]: true }));
        try {
            await templateSetService.createPlaceholder(templateSetId, {
                slug, title: newItem.title, type: 'Steps', description: '', options: '', order: placeholderList.length + 1,
            });
            setPlaceholderList(prev => [...prev, newItem]);
            notifications.show({ title: 'Step break added', color: 'blue', autoClose: 1500 });
        } catch (err) {
            notifications.show({ title: 'Failed to add step', message: err.message, color: 'red' });
        } finally {
            setPlaceholderSaving(prev => ({ ...prev, [slug]: false }));
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-10">
                <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            {reordering && (
                <div className="flex items-center gap-2">
                    <div className="w-3 h-3 border-2 border-zinc-400 border-t-transparent rounded-full animate-spin" />
                    <span className="text-xs text-zinc-500 dark:text-zinc-400">Saving order…</span>
                </div>
            )}

            {placeholderList.length === 0 ? (
                <div className="rounded-lg border border-dashed border-zinc-300 dark:border-zinc-600 p-6 text-center">
                    <p className="text-sm text-zinc-500 dark:text-zinc-400">No placeholders yet. Save the document with {'{{slug}}'} tokens to auto-detect them.</p>
                </div>
            ) : (
                <DndContext
                    sensors={dndSensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext items={placeholderList.map(p => p.slug)} strategy={verticalListSortingStrategy}>
                        <div className="flex flex-col gap-1">
                            {placeholderList.map(item => (
                                <SortablePlaceholderItem
                                    key={item.slug}
                                    item={item}
                                    saving={!!placeholderSaving[item.slug]}
                                    onUpdate={(field, value) => {
                                        setDirty(true);
                                        setPlaceholderList(prev =>
                                            prev.map(p => p.slug === item.slug ? { ...p, [field]: value } : p)
                                        );
                                    }}
                                    onSave={() => handlePlaceholderSave(item.slug)}
                                    onDelete={() => handleDeletePlaceholder(item.slug)}
                                />
                            ))}
                        </div>
                    </SortableContext>
                </DndContext>
            )}

            <button
                className="px-3 py-1.5 text-sm rounded-md border border-violet-300 dark:border-violet-700 text-violet-700 dark:text-violet-300 hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors inline-flex items-center gap-1.5"
                onClick={handleAddStepBreak}
            >
                <IconSeparator size={13} />
                Add Step Break
            </button>
        </div>
    );
});

export default PlaceholderManager;
